import { Link } from "@tanstack/react-router";
import { BadgeCheck, Megaphone, Music, ShieldCheck, Sparkles, Target } from "lucide-react";
import { ContactForm } from "@/components/contact-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PROMOTION_PLANS } from "@/lib/marketplace/promotions";
import { useT } from "@/i18n";



const STEP_KEYS = ["step1", "step2", "step3", "step4"] as const;
const CHANNEL_KEYS = [
  { key: "featuring", icon: Sparkles, live: true },
  { key: "sound", icon: Music, live: false },
  { key: "creator", icon: Megaphone, live: false },
  { key: "ads", icon: Target, live: false },
];
const RULE_KEYS = ["rule1", "rule2", "rule3", "rule4", "rule5", "rule6", "rule7"] as const;
const FAQ_KEYS = ["faq1", "faq2", "faq3", "faq4", "faq5", "faq6", "faq7", "faq8"] as const;

export function PromoteHowItWorks() {
  const { t } = useT();

  const featureLine = PROMOTION_PLANS.map((p) => t("hiw.promote.featureLineItem", { days: p.days, heat: p.heat })).join(", ");

  const STEPS = STEP_KEYS.map((k) => ({
    key: k,
    title: t(`hiw.promote.${k}.title`),
    body: t(`hiw.promote.${k}.body`),
  }));
  const CHANNELS = CHANNEL_KEYS.map((c) => ({
    ...c,
    title: t(`hiw.promote.channel.${c.key}.title`),
    body: t(`hiw.promote.channel.${c.key}.body`, { featureLine }),
  }));
  const RULES = RULE_KEYS.map((k) => t(`hiw.promote.${k}`, { featureLine }));
  const FAQ: [string, string][] = FAQ_KEYS.map((k) => [
    t(`hiw.promote.${k}.q`),
    t(`hiw.promote.${k}.a`, { featureLine }),
  ]);

  return (
    <div>
      <p className="mt-8 max-w-2xl text-muted-foreground">{t("hiw.promote.intro")}</p>

      <section className="mt-12" aria-labelledby="promote-steps-heading">
        <div className="flex items-center gap-2">
          <Megaphone className="size-5 text-primary" aria-hidden="true" />
          <h2 id="promote-steps-heading" className="font-display text-2xl font-bold">
            {t("hiw.promote.stepsHeading")}
          </h2>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <ol className="contents list-none">
            {STEPS.map((s) => (
              <li key={s.key} className="card-surface p-5">
                <h3 className="font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
          <div className="card-surface flex flex-col justify-center p-5">
            <h3 className="font-display text-lg font-bold">{t("hiw.promote.ctaTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("hiw.promote.ctaBody")}</p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <Button asChild variant="hot" className="w-full sm:w-auto">
                <Link to="/marketplace/sell">{t("hiw.promote.featureService")}</Link>
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/promote">{t("hiw.promote.promotePage")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-14" aria-labelledby="promote-channels-heading">
        <h2 id="promote-channels-heading" className="font-display text-2xl font-bold">
          {t("hiw.promote.channelsHeading")}
        </h2>
        <ul className="mt-5 grid list-none gap-4 sm:grid-cols-2">
          {CHANNELS.map((c) => (
            <li key={c.key} className="card-surface p-5">
              <div className="flex items-center gap-2">
                <c.icon className="size-5 text-primary" aria-hidden="true" />
                <h3 className="font-display text-lg font-bold">{c.title}</h3>
              </div>
              <Badge variant="outline" className="mt-3">
                {c.live ? t("hiw.promote.liveNow") : t("hiw.promote.comingSoon")}
              </Badge>
              <p className="mt-2 text-sm text-muted-foreground">{c.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-14" aria-labelledby="promote-plans-heading">
        <h2 id="promote-plans-heading" className="font-display text-2xl font-bold">
          {t("hiw.promote.plansHeading")}
        </h2>
        <div className="card-surface mt-5 grid gap-4 p-6 sm:grid-cols-2">
          {PROMOTION_PLANS.map((p) => (
            <div key={t(p.labelKey)}>
              <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                {t(p.labelKey)}
              </p>
              <p className="mt-1 font-display text-2xl font-bold text-primary">{p.heat} Heat</p>
              <p className="text-xs text-muted-foreground">
                {t("hiw.promote.daysFeatured", { days: p.days })} — {t(p.blurbKey)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-14" aria-labelledby="promote-rules-heading">
        <div className="flex items-center gap-2">
          <ShieldCheck className="size-5 text-primary" aria-hidden="true" />
          <h2 id="promote-rules-heading" className="font-display text-2xl font-bold">
            {t("hiw.promote.rulesHeading")}
          </h2>
        </div>
        <div className="card-surface mt-5 p-6">
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            {RULES.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-14" aria-labelledby="promote-faq-heading">
        <div className="flex items-center gap-2">
          <BadgeCheck className="size-5 text-primary" aria-hidden="true" />
          <h2 id="promote-faq-heading" className="font-display text-2xl font-bold">
            {t("hiw.promote.faqHeading")}
          </h2>
        </div>
        <div className="mt-5 divide-y divide-border rounded-xl border border-border">
          {FAQ.map(([q, a], i) => (
            <details key={q} open={i < 3} className="group px-5 py-4 [&_summary::-webkit-details-marker]:hidden">
              <summary className="cursor-pointer list-none font-display font-bold">{q}</summary>
              <p className="mt-2 text-sm text-muted-foreground">{a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mt-14" aria-labelledby="promote-contact-heading">
        <h2 id="promote-contact-heading" className="font-display text-2xl font-bold">
          {t("hiw.promote.contactHeading")}
        </h2>
        <div className="mt-5">
          <ContactForm defaultTopic="Promote (creator promotion)" />
        </div>
      </section>
    </div>
  );
}
